import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";

export interface HeaterStepSelection {
	/** The smallest standard step whose rated room volume covers the room. */
	kw: number;
	/** The rated maximum room volume of that step. */
	maxVolumeM3: number;
	/** True when the room is larger than even the largest step is rated for. */
	overLargestStep: boolean;
}

/**
 * The one place a heater power is chosen from a room volume.
 *
 * Manufacturers rate each heater for a room-volume range (Harvia KIP90E:
 * 9 kW for 8–14 m³). The heater fits when the room's effective volume — the
 * air volume plus the cold-surface and log-wall allowances — lies within the
 * range, so the right step is the smallest whose rated maximum covers it.
 * The old shortcut, 1 kW per m³ rounded up, put a 13 m³ room on 15 kW.
 *
 * Steps (`heater.standardKwSteps`) and their rated maxima
 * (`heater.stepMaxVolumeM3`) are rules, so an admin can extend the catalogue;
 * a step without a rated maximum is never chosen.
 */
export function selectHeaterStep(
	effectiveVolumeM3: number,
	overrides: RuleOverrides = {},
): HeaterStepSelection {
	const steps = [
		...getRuleValue<number[]>("heater.standardKwSteps", overrides),
	].sort((a, b) => a - b);
	const maxByStep = getRuleValue<Record<string, number>>(
		"heater.stepMaxVolumeM3",
		overrides,
	);
	const rated = steps
		.map((kw) => ({ kw, maxVolumeM3: maxByStep[String(kw)] }))
		.filter(
			(step): step is { kw: number; maxVolumeM3: number } =>
				typeof step.maxVolumeM3 === "number" && step.maxVolumeM3 > 0,
		);
	if (rated.length === 0) {
		throw new Error(
			"heater.stepMaxVolumeM3 rates none of heater.standardKwSteps",
		);
	}
	const fit = rated.find((step) => step.maxVolumeM3 >= effectiveVolumeM3);
	if (fit) return { ...fit, overLargestStep: false };
	const largest = rated[rated.length - 1];
	return { ...largest, overLargestStep: true };
}
